import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Loader2, KeyRound, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [state, setState] = useState({ loading: true, valid: false, email: "", error: "" });
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, valid: false, email: "", error: "Missing token" });
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/portal/auth/password/verify-link`, { params: { token } });
        setState({ loading: false, valid: true, email: r.data.email || "", error: "" });
      } catch (err) {
        setState({
          loading: false,
          valid: false,
          email: "",
          error: err?.response?.data?.detail || "This reset link is no longer valid",
        });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pwd !== pwd2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/portal/auth/password/reset-with-token`, { token, new_password: pwd });
      toast.success("Password updated — sign in with your new password");
      setTimeout(() => navigate("/portal/login", { replace: true }), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md p-8 bg-slate-900 border-slate-800 shadow-2xl" data-testid="portal-reset-password-page">
        <Link to="/portal/login" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to portal sign in
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-amber-400" strokeWidth={2} />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-white">Choose a new password</h1>
            {state.valid && state.email && <p className="text-xs text-slate-400">for {state.email}</p>}
          </div>
        </div>

        {state.loading && (
          <div className="flex items-center justify-center py-12 text-sm text-slate-400" data-testid="portal-reset-loading">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Verifying link…
          </div>
        )}

        {!state.loading && !state.valid && (
          <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 p-4 text-sm text-rose-200 flex items-start gap-2" data-testid="portal-reset-invalid">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">{state.error}</p>
              <Link to="/portal/forgot-password" className="mt-3 inline-block text-amber-300 underline" data-testid="portal-reset-request-again">
                Request a fresh reset link →
              </Link>
            </div>
          </div>
        )}

        {!state.loading && state.valid && (
          <form onSubmit={submit} className="space-y-4" data-testid="portal-reset-step-password">
            <div>
              <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider">New password</label>
              <Input
                type="password" required autoFocus minLength={8}
                value={pwd} onChange={(e) => setPwd(e.target.value)}
                placeholder="At least 8 characters"
                className="h-12 mt-1.5 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="portal-reset-new-password"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Confirm new password</label>
              <Input
                type="password" required minLength={8}
                value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                placeholder="Re-type your new password"
                className="h-12 mt-1.5 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="portal-reset-new-password-confirm"
              />
            </div>
            <Button
              type="submit" disabled={busy || pwd.length < 8 || pwd !== pwd2}
              className="w-full h-12 bg-amber-400 hover:bg-amber-500 text-amber-950 font-semibold"
              data-testid="portal-reset-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save new password
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
